import React, { useEffect, useState, useCallback } from "react";
import {
  api, adminApi, API,
  adminRewards, adminRewardSave, adminRewardDelete,
  adminRewardSettings, adminRewardSettingsSave,
  adminDifficultyGains, adminSetDifficultyGains,
  adminDailyPlan, adminDailyPlanSet, adminDailyPlanDraw, adminDailyPlanHistory,
  adminAdCampaigns, adminAdCampaignSave, adminAdCampaignDelete,
  adminAdPlacements, adminAdPlacementSave, adminAdPlacementDelete,
  adminAdsStats, adminRewardsStats, adminMediaUpload,
  adminUsers, adminUser, adminVoucherToggle, adminVoucherAdd,
} from "../lib/api";
import InstallButton from "../components/InstallButton";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import {
  Plus, Trash2, Save, BarChart3, Users, Gamepad2, Ticket, LogOut, Download, Search,
  Gift, CalendarDays, SlidersHorizontal, Megaphone, Upload, Dice5, Pencil, X, Rabbit, Trophy,
  UserCheck, Power, ChevronDown, ChevronRight, Phone,
} from "lucide-react";

const TOKEN_KEY = "yum_admin_token";
const GAMES = [
  { key: "roue", label: "🎡 Roue" },
  { key: "grattage", label: "🎫 Grattage" },
  { key: "memory", label: "🧠 Memory" },
];
const GAME_LABELS = { roue: "Roue", grattage: "Grattage", memory: "Memory", runner: "Manicou Runner", manuel: "Bon manuel", bo: "Bon manuel" };
const GAME_META = {
  roue: { emoji: "🎡", color: "#E20613" },
  grattage: { emoji: "🎫", color: "#F26C1E" },
  memory: { emoji: "🃏", color: "#9B30B5" },
  runner: { emoji: "🐿️", color: "#3ecf6f" },
  manuel: { emoji: "🎁", color: "#F9B81E" },
  bo: { emoji: "🎁", color: "#F9B81E" },
};
// Dates affichées à l'heure de la Martinique.
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { timeZone: "America/Martinique" }) : "—");
const fmtDT = (s) => (s ? new Date(s).toLocaleString("fr-FR", { timeZone: "America/Martinique", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const emptySeg = () => ({
  id: crypto.randomUUID ? crypto.randomUUID() : String(Math.random()),
  label: "Nouveau lot", color: "#F26C1E", weight: 10, is_win: true, enabled: true, icon: "",
  daily_stock: null, monthly_stock: null, total_stock: null, blocks: [],
});
// "" ou null -> null (illimité), sinon entier
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [pwd, setPwd] = useState("");
  const [authed, setAuthed] = useState(false);
  const [section, setSection] = useState("dashboard");

  const [stats, setStats] = useState(null);
  const [players, setPlayers] = useState({ total: 0, players: [] });
  const [filters, setFilters] = useState({ q: "", game: "", days: 0 });
  const [gameTab, setGameTab] = useState("roue");
  const [cfg, setCfg] = useState(null);
  const [redeemCode, setRedeemCode] = useState("");
  const [vouchers, setVouchers] = useState({ vouchers: [], counts: {} });
  const [vFilters, setVFilters] = useState({ status: "", game: "", q: "" });
  const [vSort, setVSort] = useState("date"); // date | lot | joueur
  const [vLot, setVLot] = useState(""); // filtre par lot précis

  const client = adminApi(token);

  const verify = useCallback(async (t) => {
    try {
      await adminApi(t).get("/admin/stats");
      setAuthed(true);
      return true;
    } catch (e) {
      setAuthed(false);
      localStorage.removeItem(TOKEN_KEY);
      return false;
    }
  }, []);

  useEffect(() => { if (token) verify(token); }, [token, verify]);

  const login = async () => {
    try {
      const { data } = await api.post("/admin/login", { password: pwd });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setAuthed(true);
      setPwd("");
      toast.success("Connecté");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mot de passe incorrect");
    }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); setAuthed(false); };

  const loadStats = useCallback(async () => {
    try { setStats((await client.get("/admin/stats")).data); } catch (e) { if (e?.response?.status === 401) logout(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadPlayers = useCallback(async () => {
    const { q, game, days } = filters;
    try {
      const { data } = await client.get(`/admin/players?q=${encodeURIComponent(q)}&game=${game}&days=${days}`);
      setPlayers(data);
    } catch (e) { if (e?.response?.status === 401) logout(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filters]);

  const loadCfg = useCallback(async (game) => {
    try { setCfg((await client.get(`/admin/config/${game}`)).data); } catch (e) { if (e?.response?.status === 401) logout(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadVouchers = useCallback(async () => {
    const { status, game, q } = vFilters;
    try {
      const { data } = await client.get(`/admin/vouchers?status=${status}&game=${game}&q=${encodeURIComponent(q)}`);
      setVouchers(data);
    } catch (e) { if (e?.response?.status === 401) logout(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, vFilters]);

  useEffect(() => { if (authed && section === "dashboard") loadStats(); }, [authed, section, loadStats]);
  useEffect(() => { if (authed && section === "players") loadPlayers(); }, [authed, section, loadPlayers]);
  useEffect(() => { if (authed && section === "games") loadCfg(gameTab); }, [authed, section, gameTab, loadCfg]);
  useEffect(() => { if (authed && section === "bons") loadVouchers(); }, [authed, section, loadVouchers]);

  const saveCfg = async () => {
    try {
      await client.put(`/admin/config/${gameTab}`, {
        title: cfg.title, subtitle: cfg.subtitle, tagline: cfg.tagline, enabled: cfg.enabled,
        play_limit: cfg.play_limit, max_plays: Number(cfg.max_plays || 1), play_period: cfg.play_period || "day",
        validity_days: Number(cfg.validity_days ?? 30),
        segments: cfg.segments.map((s) => ({
          ...s, weight: Number(s.weight),
          daily_stock: numOrNull(s.daily_stock), monthly_stock: numOrNull(s.monthly_stock), total_stock: numOrNull(s.total_stock),
          blocks: Array.isArray(s.blocks) ? s.blocks : [],
        })),
      });
      toast.success("Enregistré");
    } catch (e) { toast.error("Erreur de sauvegarde"); }
  };

  const validateVoucher = async (code) => {
    try {
      await client.post(`/admin/redeem/${code}`);
      toast.success("Bon validé");
      loadVouchers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };

  const toggleVoucher = async (code) => {
    try {
      const data = await adminVoucherToggle(client, code);
      toast.success(data.disabled ? "Bon désactivé" : "Bon réactivé");
      loadVouchers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };

  const updateSeg = (i, key, val) => {
    const segs = [...cfg.segments]; segs[i] = { ...segs[i], [key]: val }; setCfg({ ...cfg, segments: segs });
  };

  const redeem = async () => {
    try {
      const { data } = await client.post(`/admin/redeem/${redeemCode.trim().toUpperCase()}`);
      toast.success(`Code validé : ${data.label}`); setRedeemCode(""); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Code invalide"); }
  };

  const exportUrl = () => {
    const { q, game, days } = filters;
    return `${API}/admin/players/export?token=${encodeURIComponent(token)}&q=${encodeURIComponent(q)}&game=${game}&days=${days}`;
  };

  // ---------- Login ----------
  if (!authed) {
    return (
      <div className="min-h-screen bg-yum-pattern flex items-center justify-center px-5 py-10">
        <div className="bg-white text-ink rounded-[28px] p-8 w-full max-w-sm text-center" style={{ boxShadow: "0 12px 0 #a81a0c" }}>
          <img src="/assets/brand/logo.png" alt="YÜM" className="w-28 mx-auto mb-2 drop-shadow" />
          <span className="yum-sticker bg-sun text-ink text-[13px] inline-flex mb-4" style={{ transform: "rotate(3deg)", boxShadow: "0 4px 0 rgba(0,0,0,.12)" }}>🎛️ Back-office</span>
          <h1 className="font-display font-bold text-2xl leading-tight">Espace de gestion</h1>
          <p className="text-ink/60 text-sm mb-5">Yüm World — accès réservé</p>
          <input
            data-testid="admin-password-input" type="password" value={pwd}
            onChange={(e) => setPwd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()}
            placeholder="Mot de passe"
            className="w-full bg-cream border-2 border-transparent rounded-2xl px-4 py-3 mb-4 text-ink text-center outline-none focus:border-brand placeholder:text-ink/30"
          />
          <button data-testid="admin-login-button" onClick={login} className="yum-cta yum-cta--red w-full">Se connecter →</button>
          <p className="mt-4 text-[11px] text-ink/40 font-display">Accès par URL directe uniquement</p>
        </div>
      </div>
    );
  }

  const NAV = [
    { k: "dashboard", label: "Tableau de bord", icon: BarChart3 },
    { k: "players", label: "Joueurs", icon: Users },
    { k: "users", label: "Utilisateurs", icon: UserCheck },
    { k: "games", label: "Jeux & lots", icon: Gamepad2 },
    { k: "bons", label: "Bons gagnés", icon: Ticket },
    { k: "runner", label: "Manicou Runner", icon: Rabbit },
  ];

  return (
    <div className="min-h-screen bg-[#14161a] text-[#e7ebf1] flex flex-col md:flex-row">
      <aside className="md:flex-none md:w-52 bg-[#101317] border-b md:border-b-0 md:border-r border-[#2c323b] md:min-h-screen flex md:flex-col sticky top-0 z-30">
        <div className="hidden md:block font-black text-[#F9B81E] text-sm px-4 py-3 border-b border-[#2c323b]">YÜM · Gestion</div>
        <div className="flex md:flex-col gap-1 overflow-x-auto p-2 md:p-3 flex-1 no-scrollbar">
          {NAV.map((n) => (
            <button key={n.k} data-testid={`nav-${n.k}`} onClick={() => setSection(n.k)}
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg text-left whitespace-nowrap flex-none ${section === n.k ? "bg-[#F9B81E] text-[#1a1206] font-black" : "text-[#9aa3b2] hover:bg-white/5"}`}>
              <n.icon size={15} /> {n.label}
            </button>
          ))}
        </div>
        <div className="hidden md:block mt-auto px-3 pb-2">
          <InstallButton kind="admin" className="w-full text-[12px] font-bold text-[#1a1206] bg-[#F9B81E] px-3 py-2 rounded-lg" label="📲 Installer le back-office" />
        </div>
        <button onClick={logout} className="flex items-center gap-2 text-sm text-[#7d8590] px-3 py-2 md:pb-3 hover:text-white flex-none"><LogOut size={15} /> <span className="hidden md:inline">Déconnexion</span></button>
      </aside>

      <main className="flex-1 p-5 overflow-x-auto">
        {/* Tableau de bord */}
        {section === "dashboard" && stats && (
          <>
            <h2 className="text-2xl font-black mb-1">Tableau de bord</h2>
            <p className="text-sm text-[#7d8590] mb-5">Vue d'ensemble de YÜM World 🎮</p>

            {/* Grandes cases clés */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
              {[
                { l: "Joueurs", v: stats.players, emoji: "👥", from: "#7c4dff", to: "#5b2ed6" },
                { l: "Bons gagnés", v: stats.wins, emoji: "🎁", from: "#F9B81E", to: "#E08a00", dark: true },
                { l: "Bons utilisés", v: stats.redeemed, emoji: "✅", from: "#3ecf6f", to: "#1a9e4b", dark: true },
                { l: "Comptes créés", v: stats.accounts, emoji: "👤", from: "#FF3A1D", to: "#c40510" },
              ].map((c) => (
                <div key={c.l} className="rounded-3xl p-5 shadow-lg relative overflow-hidden"
                  style={{ background: `linear-gradient(135deg, ${c.from}, ${c.to})` }}>
                  <div className="text-3xl mb-1">{c.emoji}</div>
                  <div className={`text-4xl sm:text-5xl font-black tabular-nums leading-none ${c.dark ? "text-[#1a1206]" : "text-white"}`}>{c.v}</div>
                  <div className={`text-sm font-bold mt-1 ${c.dark ? "text-[#1a1206]/70" : "text-white/80"}`}>{c.l}</div>
                </div>
              ))}
            </div>

            {/* Par jeu : une grande carte par jeu */}
            <h3 className="font-black text-lg mb-3">Par jeu 🎯</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 mb-8">
              {(stats.games || []).map((g) => {
                const m = GAME_META[g.game] || { emoji: "🎮", color: "#9aa3b2" };
                return (
                  <div key={g.game} className="bg-[#1b1f26] border border-[#2c323b] rounded-2xl p-4 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl grid place-items-center text-3xl flex-none" style={{ background: m.color + "22" }}>{m.emoji}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-black text-white">{GAME_LABELS[g.game] || g.game}</div>
                      <div className="flex gap-3 mt-1 text-xs">
                        <span className="text-[#9aa3b2]"><b className="text-white text-base">{g.joueurs}</b> joueurs</span>
                        <span className="text-[#9aa3b2]"><b className="text-white text-base">{g.parties}</b> parties</span>
                      </div>
                    </div>
                    <div className="text-right flex-none">
                      <div className="text-2xl font-black text-[#F9B81E] tabular-nums leading-none">{g.gagnes}</div>
                      <div className="text-[10px] text-[#7d8590]">bons gagnés</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Utilisation des bons : courbe horaire des scans + bons les plus utilisés */}
            <div className="mb-6">
              <h3 className="font-black text-lg mb-1">Utilisation des bons ⏰</h3>
              <p className="text-xs text-[#7d8590] mb-3">
                Quand les bons sont scannés en caisse (heure locale Martinique) — {stats.redeemed || 0} scan{(stats.redeemed || 0) > 1 ? "s" : ""} au total.
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-[#1b1f26] border border-[#2c323b] rounded-2xl p-4">
                  {(stats.redeemed || 0) === 0 ? (
                    <div className="h-[240px] grid place-items-center text-[#7d8590] text-sm">Aucun bon scanné pour l'instant.</div>
                  ) : (
                    <div style={{ width: "100%", height: 240 }}>
                      <ResponsiveContainer>
                        <AreaChart data={stats.redeem_by_hour} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                          <defs>
                            <linearGradient id="gRedeem" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#F9B81E" stopOpacity={0.55} />
                              <stop offset="100%" stopColor="#F9B81E" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#2c323b" vertical={false} />
                          <XAxis dataKey="hour" tickFormatter={(h) => `${h}h`} interval={1} stroke="#7d8590" fontSize={11} tickLine={false} />
                          <YAxis allowDecimals={false} stroke="#7d8590" fontSize={11} tickLine={false} axisLine={false} width={32} />
                          <Tooltip
                            cursor={{ stroke: "#F9B81E", strokeOpacity: 0.3 }}
                            contentStyle={{ background: "#14171b", border: "1px solid #2c323b", borderRadius: 12, fontSize: 12 }}
                            labelStyle={{ color: "#F9B81E", fontWeight: 700 }}
                            labelFormatter={(h) => `${h}h – ${((h + 1) % 24)}h`}
                            formatter={(v) => [`${v} bon${v > 1 ? "s" : ""}`, "Scannés"]}
                          />
                          <Area type="monotone" dataKey="count" stroke="#F9B81E" strokeWidth={2.5} fill="url(#gRedeem)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
                <div className="bg-[#1b1f26] border border-[#2c323b] rounded-2xl p-4">
                  <h4 className="font-black text-sm mb-3">Bons les plus utilisés</h4>
                  {(stats.top_redeemed || []).length === 0 ? (
                    <p className="text-[#7d8590] text-sm">—</p>
                  ) : (
                    <div className="space-y-1.5">
                      {stats.top_redeemed.slice(0, 6).map((p) => (
                        <div key={p.label} className="flex items-center justify-between gap-2">
                          <span className="text-sm truncate">{p.label}</span>
                          <span className="font-black text-[#3ecf6f] tabular-nums flex-none">{p.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sous-stats + répartition */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="font-black text-lg mb-3">Autres chiffres</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { l: "Parties jouées", v: stats.total_spins },
                    { l: "Bons délivrés", v: stats.claimed },
                    { l: "Opt-in WhatsApp", v: stats.consents },
                    { l: "Bons gagnés (total)", v: stats.wins },
                  ].map((c) => (
                    <div key={c.l} className="bg-[#1b1f26] border border-[#2c323b] rounded-2xl p-4">
                      <div className="text-3xl font-black text-white tabular-nums">{c.v}</div>
                      <div className="text-xs text-[#9aa3b2] mt-1">{c.l}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-black text-lg mb-3">Lots les plus gagnés</h3>
                <div className="space-y-1.5">
                  {stats.per_segment.slice(0, 8).map((p) => (
                    <div key={p.label} className="flex items-center justify-between bg-[#1b1f26] rounded-xl px-4 py-2.5 border border-[#232830]">
                      <span className="text-sm truncate">{p.label}</span><span className="font-black text-[#F9B81E] tabular-nums ml-2">{p.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Joueurs */}
        {section === "players" && (
          <>
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-lg font-black">Joueurs</h2>
              <span className="text-sm text-[#9aa3b2]">{players.total} participations</span>
              <a href={exportUrl()} data-testid="export-csv" className="ml-auto flex items-center gap-2 bg-[#F9B81E] text-[#1a1206] font-black text-sm px-4 py-2 rounded-lg">
                <Download size={15} /> Exporter CSV
              </a>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-[#14171b] border border-[#2c323b] rounded-lg px-3">
                <Search size={14} className="text-[#7d8590]" />
                <input data-testid="players-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && loadPlayers()}
                  placeholder="Prénom ou numéro…" className="flex-1 bg-transparent py-2 text-sm outline-none" />
              </div>
              <select value={filters.game} onChange={(e) => setFilters({ ...filters, game: e.target.value })} className="bg-[#14171b] border border-[#2c323b] rounded-lg px-3 py-2 text-sm">
                <option value="">Tous les jeux</option><option value="roue">Roue</option><option value="grattage">Grattage</option>
              </select>
              <select value={filters.days} onChange={(e) => setFilters({ ...filters, days: Number(e.target.value) })} className="bg-[#14171b] border border-[#2c323b] rounded-lg px-3 py-2 text-sm">
                <option value="0">Toute période</option><option value="1">Aujourd'hui</option><option value="7">7 jours</option><option value="30">30 jours</option>
              </select>
              <button onClick={loadPlayers} className="bg-white/10 rounded-lg px-4 py-2 text-sm font-bold">Filtrer</button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-[#232830]">
              <table className="w-full text-sm" data-testid="players-table">
                <thead><tr className="text-[11px] uppercase text-[#7d8590]">
                  {["Prénom", "Téléphone", "Jeu", "Lot", "Code", "Date", "Opt-in", "Statut"].map((h) => (
                    <th key={h} className="text-left font-mono font-normal px-3 py-2 border-b border-[#2c323b] whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {players.players.map((p, i) => (
                    <tr key={i} className="border-b border-[#232830]">
                      <td className="px-3 py-2">{p.first_name} {p.last_name}</td>
                      <td className="px-3 py-2 font-mono text-[#9aa3b2] tabular-nums whitespace-nowrap">{p.phone}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${p.game === "grattage" ? "bg-[#14283a] text-[#7cc3ff]" : "bg-[#3a1420] text-[#ff8aa8]"}`}>{GAME_LABELS[p.game] || p.game}</span></td>
                      <td className="px-3 py-2 whitespace-nowrap">{p.label}</td>
                      <td className="px-3 py-2 font-mono text-[#F9B81E] text-xs">{p.code}</td>
                      <td className="px-3 py-2 text-[#9aa3b2] tabular-nums whitespace-nowrap">{fmtDT(p.claimed_at)}</td>
                      <td className="px-3 py-2">{p.consent ? <span className="text-[10px] text-[#F9B81E]">Oui</span> : <span className="text-[10px] text-[#7d8590]">Non</span>}</td>
                      <td className="px-3 py-2">{p.redeemed ? <span className="text-[10px] text-[#8b94a0]">Utilisé</span> : <span className="text-[10px] text-[#3ecf6f]">Valide</span>}</td>
                    </tr>
                  ))}
                  {players.players.length === 0 && <tr><td colSpan="8" className="px-3 py-6 text-center text-[#7d8590]">Aucun joueur pour ces filtres.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Jeux & lots */}
        {section === "games" && (
          <>
            <h2 className="text-lg font-black mb-3">Jeux & lots</h2>
            <div className="flex gap-2 mb-4">
              {GAMES.map((g) => (
                <button key={g.key} data-testid={`gametab-${g.key}`} onClick={() => setGameTab(g.key)}
                  className={`text-sm px-4 py-1.5 rounded-full border ${gameTab === g.key ? "bg-[#F9B81E] text-[#1a1206] font-black border-[#F9B81E]" : "bg-[#14171b] text-[#9aa3b2] border-[#2c323b]"}`}>{g.label}</button>
              ))}
            </div>
            {cfg && (
              <div className="max-w-2xl">
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  <label className="text-xs text-[#9aa3b2]">Titre
                    <input value={cfg.title} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} className="w-full bg-white/5 rounded-lg px-3 py-2 mt-1 text-white outline-none" /></label>
                  <label className="text-xs text-[#9aa3b2]">Sous-titre
                    <input value={cfg.subtitle} onChange={(e) => setCfg({ ...cfg, subtitle: e.target.value })} className="w-full bg-white/5 rounded-lg px-3 py-2 mt-1 text-white outline-none" /></label>
                  <label className="text-xs text-[#9aa3b2]">Accroche (accueil)
                    <input value={cfg.tagline || ""} onChange={(e) => setCfg({ ...cfg, tagline: e.target.value })} className="w-full bg-white/5 rounded-lg px-3 py-2 mt-1 text-white outline-none" /></label>
                  <label className="text-xs text-[#9aa3b2]">Limite de jeu
                    <select value={cfg.play_limit} onChange={(e) => setCfg({ ...cfg, play_limit: e.target.value })} className="w-full bg-white/5 rounded-lg px-3 py-2 mt-1 text-white outline-none">
                      <option className="text-black" value="daily">1 tour par jour</option>
                      <option className="text-black" value="once">1 seul tour</option>
                      <option className="text-black" value="unlimited">Illimité</option>
                      <option className="text-black" value="custom">Nombre personnalisé…</option>
                    </select></label>
                  {cfg.play_limit === "custom" && (<>
                    <label className="text-xs text-[#9aa3b2]">Nombre de parties / device
                      <input type="number" min="1" value={cfg.max_plays ?? 1} onChange={(e) => setCfg({ ...cfg, max_plays: e.target.value })} className="w-full bg-white/5 rounded-lg px-3 py-2 mt-1 text-white outline-none" /></label>
                    <label className="text-xs text-[#9aa3b2]">Période
                      <select value={cfg.play_period || "day"} onChange={(e) => setCfg({ ...cfg, play_period: e.target.value })} className="w-full bg-white/5 rounded-lg px-3 py-2 mt-1 text-white outline-none">
                        <option className="text-black" value="day">par jour</option><option className="text-black" value="ever">au total</option>
                      </select></label>
                  </>)}
                  <label className="text-xs text-[#9aa3b2]">Validité des bons (jours)
                    <input data-testid="cfg-validity" type="number" min="0" value={cfg.validity_days ?? 30} onChange={(e) => setCfg({ ...cfg, validity_days: e.target.value })} className="w-full bg-white/5 rounded-lg px-3 py-2 mt-1 text-white outline-none" />
                    <span className="text-[10px] text-[#6b7480]">0 = sans expiration</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-[#9aa3b2] mt-1">
                    <input type="checkbox" checked={cfg.enabled !== false} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} /> Jeu actif (visible sur l'accueil)
                  </label>
                </div>

                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-[#F9B81E] text-sm">Lots & pourcentages</h3>
                  <span className="text-[11px] text-[#9aa3b2]">Poids total : {cfg.segments.filter((s) => s.enabled).reduce((a, s) => a + Number(s.weight || 0), 0)}</span>
                </div>
                <div className="space-y-2" data-testid="segments-list">
                  {cfg.segments.map((s, i) => {
                    const totalW = cfg.segments.filter((x) => x.enabled).reduce((a, x) => a + Number(x.weight || 0), 0) || 1;
                    const pct = s.enabled ? ((Number(s.weight || 0) / totalW) * 100).toFixed(1) : "0";
                    return (
                      <div key={s.id} className="flex flex-wrap items-center gap-2 bg-[#1d2127] border border-[#232830] rounded-lg p-2">
                        <input type="color" value={s.color} onChange={(e) => updateSeg(i, "color", e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent" title="Couleur de la part" />
                        <input value={s.icon || ""} onChange={(e) => updateSeg(i, "icon", e.target.value)} placeholder="🎁" title="Icône (emoji) — vide = auto" maxLength={4} className="w-12 bg-white/5 rounded-lg px-1 py-2 text-center text-lg outline-none" data-testid={`seg-icon-${i}`} />
                        <input value={s.label} onChange={(e) => updateSeg(i, "label", e.target.value)} className="flex-1 min-w-[120px] bg-white/5 rounded-lg px-3 py-2 text-white outline-none" />
                        <input type="number" min="0" value={s.weight} onChange={(e) => updateSeg(i, "weight", e.target.value)} className="w-16 bg-white/5 rounded-lg px-2 py-2 text-center text-white outline-none" />
                        <span className="text-[11px] text-[#7d8590] w-12 tabular-nums">{pct}%</span>
                        <label className="flex items-center gap-1 text-[11px] text-[#9aa3b2]"><input type="checkbox" checked={s.is_win} onChange={(e) => updateSeg(i, "is_win", e.target.checked)} /> gagnant</label>
                        <label className="flex items-center gap-1 text-[11px] text-[#9aa3b2]"><input type="checkbox" checked={s.enabled} onChange={(e) => updateSeg(i, "enabled", e.target.checked)} /> actif</label>
                        <button onClick={() => setCfg({ ...cfg, segments: cfg.segments.filter((_, j) => j !== i) })} className="text-red-400 hover:text-red-300 p-1"><Trash2 size={15} /></button>
                        {s.is_win && (
                          <div className="basis-full flex flex-wrap items-center gap-2 pt-2 mt-1 border-t border-[#232830]">
                            <span className="text-[11px] text-[#7d8590]">Plafonds :</span>
                            {[["daily_stock", "/ jour"], ["monthly_stock", "/ mois"], ["total_stock", "total"]].map(([k, lbl]) => (
                              <label key={k} className="flex items-center gap-1 text-[11px] text-[#9aa3b2]">
                                <input type="number" min="0" value={s[k] ?? ""} placeholder="∞"
                                  onChange={(e) => updateSeg(i, k, e.target.value === "" ? null : Number(e.target.value))}
                                  className="w-16 bg-white/5 rounded-lg px-2 py-1.5 text-center text-white outline-none placeholder:text-[#5b626d]" />
                                {lbl}
                              </label>
                            ))}
                            <span className="text-[10px] text-[#5b626d]">vide = illimité</span>
                          </div>
                        )}
                        {s.is_win && cfg.segments.some((o, j) => j !== i && o.is_win) && (
                          <div className="basis-full flex flex-wrap items-center gap-1.5 pt-2 mt-1 border-t border-[#232830]">
                            <span className="text-[11px] text-[#7d8590] mr-1">Si gagné, bloque :</span>
                            {cfg.segments.map((o, j) => (j === i || !o.is_win) ? null : (
                              <button key={o.id} type="button"
                                onClick={() => { const cur = Array.isArray(s.blocks) ? s.blocks : []; updateSeg(i, "blocks", cur.includes(o.id) ? cur.filter((x) => x !== o.id) : [...cur, o.id]); }}
                                className={`text-[11px] rounded-full px-2.5 py-1 border ${(s.blocks || []).includes(o.id) ? "bg-[#f0616d]/20 border-[#f0616d]/60 text-[#ff9aa5] font-bold" : "border-[#2c323b] text-[#9aa3b2]"}`}>
                                {o.icon || "🎁"} {o.label || "lot"}
                              </button>
                            ))}
                            <span className="text-[10px] text-[#5b626d]">(ce joueur ne pourra plus les gagner)</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button onClick={() => setCfg({ ...cfg, segments: [...cfg.segments, emptySeg()] })} className="flex items-center gap-1 text-sm text-[#F9B81E] font-bold"><Plus size={15} /> Ajouter un lot</button>
                  <button onClick={saveCfg} data-testid="save-config" className="ml-auto flex items-center gap-2 bg-[#F9B81E] text-[#1a1206] px-6 py-2 rounded-lg font-black text-sm"><Save size={15} /> Enregistrer</button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Bons gagnés */}
        {section === "bons" && (
          <>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <h2 className="text-lg font-black">Bons gagnés</h2>
              <div className="flex gap-2 text-xs">
                <span className="bg-[#12301d] text-[#3ecf6f] rounded-full px-2 py-1 font-bold">{vouchers.counts.valid || 0} non scannés</span>
                <span className="bg-[#2a2e35] text-[#8b94a0] rounded-full px-2 py-1 font-bold">{vouchers.counts.redeemed || 0} scannés</span>
                <span className="bg-[#3a1414] text-[#ff8a8a] rounded-full px-2 py-1 font-bold">{vouchers.counts.expired || 0} expirés</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <div className="flex items-center gap-2 flex-1 min-w-[180px] bg-[#14171b] border border-[#2c323b] rounded-lg px-3">
                <Search size={14} className="text-[#7d8590]" />
                <input data-testid="bons-search" value={vFilters.q} onChange={(e) => setVFilters({ ...vFilters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && loadVouchers()} placeholder="Code, prénom, numéro…" className="flex-1 bg-transparent py-2 text-sm outline-none" />
              </div>
              <select value={vFilters.status} onChange={(e) => setVFilters({ ...vFilters, status: e.target.value })} className="bg-[#14171b] border border-[#2c323b] rounded-lg px-3 py-2 text-sm">
                <option value="">Tous les statuts</option><option value="valid">Non scannés</option><option value="redeemed">Scannés</option><option value="expired">Expirés</option>
              </select>
              <select value={vFilters.game} onChange={(e) => setVFilters({ ...vFilters, game: e.target.value })} className="bg-[#14171b] border border-[#2c323b] rounded-lg px-3 py-2 text-sm">
                <option value="">Tous les jeux</option><option value="roue">Roue</option><option value="grattage">Grattage</option>
              </select>
              <select value={vLot} onChange={(e) => setVLot(e.target.value)} title="Filtrer par lot" className="bg-[#14171b] border border-[#2c323b] rounded-lg px-3 py-2 text-sm max-w-[190px]">
                <option value="">Tous les lots</option>
                {[...new Set(vouchers.vouchers.map((v) => v.label))].sort().map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <select value={vSort} onChange={(e) => setVSort(e.target.value)} title="Trier" className="bg-[#14171b] border border-[#2c323b] rounded-lg px-3 py-2 text-sm">
                <option value="date">Trier : récent</option>
                <option value="scan">Trier : scan récent</option>
                <option value="lot">Trier : par lot</option>
                <option value="joueur">Trier : par joueur</option>
              </select>
              <button onClick={loadVouchers} className="bg-white/10 rounded-lg px-4 py-2 text-sm font-bold">Filtrer</button>
            </div>
            <div className="flex gap-2 mb-4 max-w-md">
              <input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value)} placeholder="Valider un code à la main : YUM-XXXXX" className="flex-1 bg-[#14171b] border border-[#2c323b] rounded-lg px-4 py-2.5 outline-none uppercase text-sm" />
              <button onClick={async () => { await redeem(); loadVouchers(); }} className="bg-[#F9B81E] text-[#1a1206] px-5 rounded-lg font-black text-sm">Valider</button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-[#232830]">
              <table className="w-full text-sm" data-testid="bons-table">
                <thead><tr className="text-[11px] uppercase text-[#7d8590]">
                  {["Code", "Lot", "Jeu", "Joueur", "Gagné le", "Expire le", "Statut", ""].map((h) => (
                    <th key={h} className="text-left font-mono font-normal px-3 py-2 border-b border-[#2c323b] whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {vouchers.vouchers
                    .filter((v) => !vLot || v.label === vLot)
                    .slice()
                    .sort((a, b) =>
                      vSort === "scan" ? (b.redeemed_at || "").localeCompare(a.redeemed_at || "")
                        : vSort === "lot" ? (a.label || "").localeCompare(b.label || "") || (b.claimed_at || "").localeCompare(a.claimed_at || "")
                          : vSort === "joueur" ? (a.first_name || "").localeCompare(b.first_name || "") || (b.claimed_at || "").localeCompare(a.claimed_at || "")
                            : (b.claimed_at || "").localeCompare(a.claimed_at || ""))
                    .map((v) => (
                    <tr key={v.code} className={`border-b border-[#232830] ${v.status === "redeemed" ? "opacity-45 line-through decoration-1" : ""}`}>
                      <td className="px-3 py-2 font-mono text-[#F9B81E] text-xs">{v.code}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{v.label}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${v.game === "grattage" ? "bg-[#14283a] text-[#7cc3ff]" : "bg-[#3a1420] text-[#ff8aa8]"}`}>{GAME_LABELS[v.game] || v.game}</span></td>
                      <td className="px-3 py-2 whitespace-nowrap">{v.first_name} <span className="text-[#7d8590] font-mono text-xs">{v.phone}</span></td>
                      <td className="px-3 py-2 text-[#9aa3b2] tabular-nums whitespace-nowrap">{fmtD(v.claimed_at)}</td>
                      <td className="px-3 py-2 text-[#9aa3b2] tabular-nums whitespace-nowrap">{fmtD(v.expires_at)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] font-bold ${v.status === "valid" ? "text-[#3ecf6f]" : v.status === "expired" ? "text-[#ff8a8a]" : v.status === "disabled" ? "text-[#f0616d]" : "text-[#8b94a0]"}`}>
                          {v.status === "valid" ? "Non scanné"
                            : v.status === "expired" ? "Expiré"
                            : v.status === "disabled" ? "Désactivé"
                            : `Scanné${v.redeemed_at ? " le " + fmtD(v.redeemed_at) : ""}`}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          {v.status === "valid" && (
                            <button onClick={() => validateVoucher(v.code)} className="text-[11px] bg-[#3ecf6f] text-[#06230f] font-bold rounded px-2 py-1">Valider</button>
                          )}
                          {v.status !== "redeemed" && v.status !== "expired" && (
                            <button onClick={() => toggleVoucher(v.code)} title={v.disabled ? "Réactiver" : "Désactiver"}
                              className={`flex items-center gap-1 text-[11px] font-bold rounded px-2 py-1 border ${v.disabled ? "text-[#3ecf6f] border-[#3ecf6f]/40" : "text-[#f0616d] border-[#f0616d]/40"}`}>
                              <Power size={12} /> {v.disabled ? "Réactiver" : "Désactiver"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {vouchers.vouchers.filter((v) => !vLot || v.label === vLot).length === 0 && <tr><td colSpan="8" className="px-3 py-6 text-center text-[#7d8590]">Aucun bon pour ces filtres.</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[#6b7480] mt-3">Validation à la chaîne en boutique : utilise le <b>lien caisse</b> <code className="text-[#F9B81E]">/caisse</code> (scan caméra + PIN).</p>
          </>
        )}

        {section === "users" && <UsersSection client={client} onAuthErr={logout} />}
        {section === "runner" && <RunnerSection client={client} onAuthErr={logout} />}
      </main>
    </div>
  );
}

// ==================== P5 : helpers UI back-office ====================

const inputCls = "w-full bg-white/5 border border-[#2c323b] rounded-lg px-3 py-2 mt-1 text-white text-sm outline-none focus:border-[#F9B81E]";
const labelCls = "text-xs text-[#9aa3b2]";
const btnPrimary = "flex items-center gap-2 bg-[#F9B81E] text-[#1a1206] px-5 py-2 rounded-lg font-black text-sm disabled:opacity-50";
const cardCls = "bg-[#1d2127] border border-[#2c323b] rounded-xl";
const DOW = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"]; // 0=lundi (weekday Python)
const DOW_IDX = [0, 1, 2, 3, 4, 5, 6];
const handleErr = (e, onAuthErr, msg) => {
  if (e?.response?.status === 401) onAuthErr?.();
  toast.error(e?.response?.data?.detail || msg || "Erreur");
};

function Field({ label, hint, children }) {
  return (
    <label className={labelCls}>
      {label}
      {children}
      {hint && <span className="block text-[10px] text-[#6b7480] mt-0.5">{hint}</span>}
    </label>
  );
}

function NumField({ label, value, onChange, hint, min, step }) {
  return (
    <Field label={label} hint={hint}>
      <input type="number" min={min} step={step} value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className={inputCls} />
    </Field>
  );
}

// Bouton d'upload média → renvoie l'URL au parent.
function MediaUpload({ client, value, onChange, accept = "image/*", onAuthErr }) {
  const [busy, setBusy] = useState(false);
  const up = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await adminMediaUpload(client, file);
      onChange(url);
      toast.success("Média importé");
    } catch (e) { handleErr(e, onAuthErr, "Import impossible"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 mt-1">
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL du média" className={inputCls.replace(" mt-1", "")} />
      <label className="flex-none flex items-center gap-1 text-xs bg-white/10 rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap">
        <Upload size={14} /> {busy ? "…" : "Fichier"}
        <input type="file" accept={accept} className="hidden" onChange={(e) => up(e.target.files?.[0])} />
      </label>
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4">
      <div className={`${cardCls} w-full ${wide ? "max-w-3xl" : "max-w-lg"} my-6`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#2c323b]">
          <h3 className="font-black text-white">{title}</h3>
          <button onClick={onClose} className="text-[#9aa3b2] hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const CSVList = ({ value, onChange, placeholder }) => (
  <input value={(value || []).join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
    placeholder={placeholder} className={inputCls} />
);

// ---------- 1. Récompenses ----------

const emptyReward = (bonus = false) => ({
  name: "", image: "", description: "", daily_stock: null, monthly_stock: null, total_stock: null,
  active_from: null, active_to: null, days: [], weight: 10, max_winners_day: null,
  active: true, conditions: "", min_age: null, threshold: null,
  quota_player: null, quota_phone: null, quota_device: null, quota_ip: null,
  difficulties: [], bonus, thresholds: {},
});

const DIFF_OPTS = [["facile", "Facile"], ["normal", "Normal"], ["difficile", "Difficile"], ["extreme", "Extrême"]];

// ---------- Onglet consolidé « Manicou Runner » (sous-onglets) ----------

const RUNNER_TABS = [
  { k: "rewards", label: "Récompenses", icon: Gift },
  { k: "bonus", label: "Récompenses bonus", icon: Trophy },
  { k: "planning", label: "Planning du jour", icon: CalendarDays },
  { k: "difficulty", label: "Difficulté & gains", icon: SlidersHorizontal },
  { k: "ads", label: "Publicités", icon: Megaphone },
];

function RunnerSection({ client, onAuthErr }) {
  const [tab, setTab] = useState("rewards");
  return (
    <>
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {RUNNER_TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border ${tab === t.k ? "bg-[#F9B81E] text-[#1a1206] border-[#F9B81E] font-bold" : "bg-white/5 text-[#9aa3b2] border-[#2c323b]"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "rewards" && <RewardsSection client={client} onAuthErr={onAuthErr} />}
      {tab === "bonus" && <RewardsSection client={client} onAuthErr={onAuthErr} bonus />}
      {tab === "planning" && <PlanningSection client={client} onAuthErr={onAuthErr} />}
      {tab === "difficulty" && <DifficultySection client={client} onAuthErr={onAuthErr} />}
      {tab === "ads" && <AdsSection client={client} onAuthErr={onAuthErr} />}
    </>
  );
}

// ---------- Utilisateurs : tous les bons regroupés par personne ----------

const BON_STATUS = {
  valid: { label: "Valide", cls: "text-[#3ecf6f] bg-[#3ecf6f]/15" },
  redeemed: { label: "Utilisé", cls: "text-[#9aa3b2] bg-white/8" },
  expired: { label: "Expiré", cls: "text-[#e0a23a] bg-[#e0a23a]/15" },
  disabled: { label: "Désactivé", cls: "text-[#f0616d] bg-[#f0616d]/15" },
};
const GAME_LABEL = { roue: "Roue", grattage: "Grattage", memory: "Memory", runner: "Runner", manuel: "Manuel", bo: "Manuel" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

// Classement client selon le nombre de bons UTILISÉS : VIP ≥4, Récurrent 2-3, Ponctuel 1.
function tierOf(used) {
  if (used >= 4) return { key: "vip", label: "VIP", emoji: "👑", cls: "text-[#F9B81E] bg-[#F9B81E]/15 border-[#F9B81E]/45" };
  if (used >= 2) return { key: "recurrent", label: "Récurrent", emoji: "⭐", cls: "text-[#cf9bf5] bg-[#cf9bf5]/15 border-[#cf9bf5]/40" };
  if (used >= 1) return { key: "ponctuel", label: "Ponctuel", emoji: "🙂", cls: "text-[#7fb2e6] bg-[#7fb2e6]/12 border-[#7fb2e6]/35" };
  return null;
}
const TIER_ORDER = [
  { key: "vip", label: "👑 VIP", hint: "4 bons utilisés ou +" },
  { key: "recurrent", label: "⭐ Récurrents", hint: "2 à 3 bons utilisés" },
  { key: "ponctuel", label: "🙂 Ponctuels", hint: "1 bon utilisé" },
];
// Client à relancer : aucune activité (bon gagné ou utilisé) depuis 30 jours.
const isInactive = (u) => u.last_at && Date.now() - new Date(u.last_at).getTime() > 30 * 24 * 3600 * 1000;

function UsersSection({ client, onAuthErr }) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(null);        // téléphone déplié
  const [detail, setDetail] = useState(null);    // { user, bons }
  const [busy, setBusy] = useState(false);
  const [addFor, setAddFor] = useState(null);    // { phone, name }
  const [tierFilter, setTierFilter] = useState(null); // filtre de rang (vip/recurrent/ponctuel)

  const load = useCallback(async () => {
    try { setUsers((await adminUsers(client, q)).users || []); }
    catch (e) { handleErr(e, onAuthErr, "Chargement impossible"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const loadDetail = async (phone) => { try { setDetail(await adminUser(client, phone)); } catch (e) { handleErr(e, onAuthErr, "Chargement impossible"); } };
  const openUser = async (phone) => {
    if (open === phone) { setOpen(null); setDetail(null); return; }
    setOpen(phone); setDetail(null); setBusy(true);
    await loadDetail(phone); setBusy(false);
  };
  const toggle = async (code, phone) => {
    try { await adminVoucherToggle(client, code); toast.success("Bon mis à jour"); loadDetail(phone); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };
  const fullName = (u) => [u.first_name, u.last_name].filter(Boolean).join(" ") || "—";

  const counts = users.reduce((a, u) => { const t = tierOf(u.utilises); if (t) a[t.key] = (a[t.key] || 0) + 1; return a; }, {});
  counts.inactif = users.filter(isInactive).length;
  const shown = tierFilter === "inactif" ? users.filter(isInactive)
    : tierFilter ? users.filter((u) => (tierOf(u.utilises) || {}).key === tierFilter) : users;

  // Export CSV du segment affiché (pour une campagne WhatsApp/SMS par ex.)
  const exportCsv = () => {
    const lines = ["Nom;Téléphone;Rang;Bons utilisés;Bons valides;Total;Dernière visite;Dernière activité;Opt-in WhatsApp"];
    shown.forEach((u) => {
      const t = tierOf(u.utilises);
      lines.push([fullName(u), u.phone, t ? t.label : "—", u.utilises, u.valides, u.total,
        fmtDate(u.last_visit), fmtDate(u.last_at), u.opt_in ? "Oui" : "Non"].join(";"));
    });
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `yum-clients${tierFilter ? "-" + tierFilter : ""}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-1">
        <h2 className="text-lg font-black">Utilisateurs</h2>
        <span className="text-sm text-[#9aa3b2]">{users.length}</span>
        <button onClick={exportCsv} className="ml-auto flex items-center gap-2 bg-[#F9B81E] text-[#1a1206] font-black text-sm px-4 py-2 rounded-lg">
          <Download size={15} /> Exporter ({shown.length})
        </button>
      </div>
      <p className="text-xs text-[#7d8590] mb-4">Tous les bons gagnés par une même personne, regroupés par numéro. Déplie un utilisateur pour voir ses bons (gagné le, expiration, statut), les désactiver/réactiver ou en ajouter un.</p>
      <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-[#14171b] border border-[#2c323b] rounded-lg px-3 mb-4 max-w-md">
        <Search size={14} className="text-[#7d8590]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Nom ou numéro…" className="flex-1 bg-transparent py-2 text-sm outline-none" />
        {q && <button onClick={() => { setQ(""); setTimeout(load, 0); }} className="text-[#7d8590]"><X size={14} /></button>}
      </div>

      {/* Classement : filtres par rang (basé sur les bons utilisés) */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setTierFilter(null)}
          className={`text-sm px-3 py-1.5 rounded-full border ${!tierFilter ? "bg-white/10 text-white border-white/30 font-bold" : "bg-white/5 text-[#9aa3b2] border-[#2c323b]"}`}>
          Tous ({users.length})
        </button>
        {TIER_ORDER.map((t) => (
          <button key={t.key} onClick={() => setTierFilter(tierFilter === t.key ? null : t.key)} title={t.hint}
            className={`text-sm px-3 py-1.5 rounded-full border ${tierFilter === t.key ? "bg-[#F9B81E] text-[#1a1206] border-[#F9B81E] font-black" : "bg-white/5 text-[#9aa3b2] border-[#2c323b]"}`}>
            {t.label} ({counts[t.key] || 0})
          </button>
        ))}
        <button onClick={() => setTierFilter(tierFilter === "inactif" ? null : "inactif")} title="Aucune activité depuis 30 jours — à relancer"
          className={`text-sm px-3 py-1.5 rounded-full border ${tierFilter === "inactif" ? "bg-[#F9B81E] text-[#1a1206] border-[#F9B81E] font-black" : "bg-white/5 text-[#9aa3b2] border-[#2c323b]"}`}>
          😴 Inactifs 30 j+ ({counts.inactif || 0})
        </button>
      </div>

      <div className="flex flex-col gap-2">
        {shown.map((u) => {
          const tier = tierOf(u.utilises);
          const isOpen = open === u.phone;
          return (
            <div key={u.phone} className="rounded-xl border border-[#232830] bg-[#14171b] overflow-hidden">
              <button onClick={() => openUser(u.phone)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03]">
                {isOpen ? <ChevronDown size={18} className="text-[#F9B81E]" /> : <ChevronRight size={18} className="text-[#7d8590]" />}
                <div className="min-w-0">
                  <div className="font-bold truncate flex items-center gap-2">
                    {fullName(u)}
                    {tier && <span className={`text-[11px] font-black px-2 py-0.5 rounded-full border ${tier.cls}`}>{tier.emoji} {tier.label}</span>}
                  </div>
                  <div className="text-xs text-[#9aa3b2] flex items-center gap-1"><Phone size={11} /> {u.phone}</div>
                </div>
                <div className="ml-auto flex items-center gap-2 text-xs">
                  {u.last_visit && <span className="px-2 py-1 rounded-full text-[#7d8590] bg-white/5 hidden sm:inline">Vu le {fmtDate(u.last_visit)}</span>}
                  {u.opt_in && <span title="Opt-in WhatsApp" className="px-1.5 py-1 rounded-full bg-[#3ecf6f]/15">💬</span>}
                  {u.utilises > 0 && <span className="px-2 py-1 rounded-full text-[#9aa3b2] bg-white/8">{u.utilises} utilisé{u.utilises > 1 ? "s" : ""}</span>}
                  {u.valides > 0 && <span className="px-2 py-1 rounded-full font-bold text-[#3ecf6f] bg-[#3ecf6f]/15">{u.valides} valide{u.valides > 1 ? "s" : ""}</span>}
                  <span className="px-2 py-1 rounded-full text-[#9aa3b2] bg-white/8">{u.total} bon{u.total > 1 ? "s" : ""}</span>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-[#232830] p-3">
                  {/* Fiche client synthétique (inspirée Hey Pongo) */}
                  {detail?.profile && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
                      {[
                        ["Client depuis", fmtDate(detail.profile.client_since)],
                        ["Dernière visite", fmtDate(detail.profile.last_visit)],
                        ["Âge", detail.user.age != null ? `${detail.user.age} ans` : "—"],
                        ["Jeu préféré", GAME_LABEL[detail.profile.favorite_game] || detail.profile.favorite_game || "—"],
                        ["Lot préféré", detail.profile.favorite_label || "—"],
                        ["Opt-in WhatsApp", detail.profile.opt_in ? "✅ Oui" : "—"],
                      ].map(([l, v]) => (
                        <div key={l} className="bg-white/[0.04] border border-[#232830] rounded-lg px-3 py-2">
                          <div className="text-[10px] uppercase tracking-wide text-[#7d8590]">{l}</div>
                          <div className="text-sm font-bold truncate" title={String(v)}>{v}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {detail?.user?.email && <p className="text-xs text-[#9aa3b2] mb-3">✉️ {detail.user.email}</p>}
                  <div className="flex items-center gap-2 mb-3">
                    <button onClick={() => setAddFor({ phone: u.phone, name: fullName(u) })} className={btnPrimary}><Plus size={15} /> Ajouter un bon</button>
                    <a href={`https://wa.me/${u.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 bg-[#25D366]/15 text-[#3ecf6f] border border-[#3ecf6f]/40 px-4 py-2 rounded-lg font-black text-sm">
                      💬 WhatsApp
                    </a>
                  </div>
                  {busy && !detail ? (
                    <div className="text-sm text-[#7d8590] px-1 py-3">Chargement…</div>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-[#232830]">
                      <table className="w-full text-sm">
                        <thead><tr className="text-[11px] uppercase text-[#7d8590]">
                          {["Lot", "Jeu", "Gagné le", "Expire le", "Statut", "Code", ""].map((h, i) => (
                            <th key={i} className="text-left font-mono font-normal px-3 py-2 border-b border-[#2c323b] whitespace-nowrap">{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {(detail?.bons || []).map((b) => {
                            const st = BON_STATUS[b.status] || BON_STATUS.valid;
                            return (
                              <tr key={b.code} className="border-b border-[#232830]">
                                <td className="px-3 py-2 font-bold">{b.label}</td>
                                <td className="px-3 py-2 text-[#9aa3b2] whitespace-nowrap">{GAME_LABEL[b.game] || b.game}</td>
                                <td className="px-3 py-2 tabular-nums whitespace-nowrap">{fmtDate(b.claimed_at)}</td>
                                <td className="px-3 py-2 tabular-nums whitespace-nowrap">{fmtDate(b.expires_at)}</td>
                                <td className="px-3 py-2"><span className={`text-[11px] font-bold px-2 py-1 rounded-full whitespace-nowrap ${st.cls}`}>{st.label}</span></td>
                                <td className="px-3 py-2 font-mono text-xs text-[#9aa3b2] whitespace-nowrap">{b.code}</td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {b.status === "redeemed" ? (
                                    <span className="text-[11px] text-[#7d8590]">—</span>
                                  ) : (
                                    <button onClick={() => toggle(b.code, u.phone)}
                                      className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-lg border ${b.disabled ? "text-[#3ecf6f] border-[#3ecf6f]/40" : "text-[#f0616d] border-[#f0616d]/40"}`}>
                                      <Power size={13} /> {b.disabled ? "Réactiver" : "Désactiver"}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {detail && detail.bons.length === 0 && <tr><td colSpan="7" className="px-3 py-5 text-center text-[#7d8590]">Aucun bon.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {shown.length === 0 && <div className="text-center text-[#7d8590] py-10">{tierFilter ? "Aucun utilisateur dans ce rang." : "Aucun utilisateur."}</div>}
      </div>

      {addFor && <AddBonModal client={client} target={addFor} onAuthErr={onAuthErr}
        onClose={() => setAddFor(null)}
        onDone={() => { setAddFor(null); if (open === addFor.phone) loadDetail(addFor.phone); load(); }} />}
    </>
  );
}

function AddBonModal({ client, target, onClose, onDone, onAuthErr }) {
  const [form, setForm] = useState({ label: "", game: "manuel", validity_days: 30 });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.label.trim()) { toast.error("Lot requis"); return; }
    setSaving(true);
    try {
      await adminVoucherAdd(client, { phone: target.phone, label: form.label.trim(), game: form.game, validity_days: Number(form.validity_days) || 0 });
      toast.success("Bon ajouté"); onDone();
    } catch (e) { handleErr(e, onAuthErr, "Ajout impossible"); toast.error(e?.response?.data?.detail || "Ajout impossible"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Ajouter un bon" onClose={onClose}>
      <p className="text-sm text-[#9aa3b2] mb-3">Pour <b className="text-white">{target.name}</b> · {target.phone}</p>
      <div className="flex flex-col gap-3">
        <Field label="Lot offert"><input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="ex. Crêpe sucrée offerte" className={inputCls} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <label className={labelCls}>Origine
            <select value={form.game} onChange={(e) => setForm({ ...form, game: e.target.value })} className={inputCls}>
              <option value="manuel">Manuel (geste commercial)</option>
              <option value="roue">Roue</option>
              <option value="grattage">Grattage</option>
              <option value="memory">Memory</option>
              <option value="runner">Runner</option>
            </select>
          </label>
          <NumField label="Validité (jours)" value={form.validity_days} onChange={(v) => setForm({ ...form, validity_days: v })} min={0} hint="0 = sans expiration" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="text-sm text-[#9aa3b2] px-4">Annuler</button>
        <button onClick={save} disabled={saving} className={btnPrimary}><Save size={15} /> Créer le bon</button>
      </div>
    </Modal>
  );
}

function RewardsSection({ client, onAuthErr, bonus = false }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null); // {reward, id}
  const load = useCallback(async () => {
    try {
      const all = await adminRewards(client);
      setRows(all.filter((r) => (bonus ? !!r.bonus : !r.bonus)));
    } catch (e) { handleErr(e, onAuthErr, "Chargement impossible"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const r = edit.reward;
    if (!r.name.trim()) { toast.error("Nom requis"); return; }
    try {
      await adminRewardSave(client, { ...r, weight: Number(r.weight) || 0 }, edit.id);
      toast.success("Récompense enregistrée");
      setEdit(null); load();
    } catch (e) { handleErr(e, onAuthErr, "Enregistrement impossible"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cette récompense ?")) return;
    try { await adminRewardDelete(client, id); toast.success("Supprimée"); load(); }
    catch (e) { handleErr(e, onAuthErr, "Suppression impossible"); }
  };

  const set = (k, v) => setEdit((s) => ({ ...s, reward: { ...s.reward, [k]: v } }));
  const toggleDay = (d) => set("days", edit.reward.days.includes(d) ? edit.reward.days.filter((x) => x !== d) : [...edit.reward.days, d].sort());
  const toggleDiff = (lvl) => { const cur = edit.reward.difficulties || []; set("difficulties", cur.includes(lvl) ? cur.filter((x) => x !== lvl) : [...cur, lvl]); };
  const setThr = (lvl, v) => setEdit((s) => ({ ...s, reward: { ...s.reward, thresholds: { ...(s.reward.thresholds || {}), [lvl]: v } } }));

  return (
    <>
      <div className="flex items-center gap-3 mb-1">
        <h2 className="text-lg font-black">{bonus ? "Récompenses bonus (paliers)" : "Récompenses Runner"}</h2>
        <span className="text-sm text-[#9aa3b2]">{rows.length} {bonus ? "palier(s)" : "au catalogue"}</span>
        <button onClick={() => setEdit({ reward: emptyReward(bonus), id: null })} className={`${btnPrimary} ml-auto`}><Plus size={15} /> Nouvelle</button>
      </div>
      <p className="text-xs text-[#7d8590] mb-4 max-w-2xl">
        {bonus
          ? "Ces lots s'ajoutent à la barre du jeu comme paliers, EN PLUS du gain du jour. Chaque palier se débloque à son « Seuil de score ». Le joueur gagne le meilleur lot atteint. Ils ne passent pas par le tirage/planning."
          : "Catalogue des lots gagnables au tirage du jour. Pour des récompenses supplémentaires par palier de score, voir l'onglet « Récompenses bonus »."}
      </p>
      <div className="overflow-x-auto rounded-lg border border-[#232830]">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase text-[#7d8590]">
            {["", "Nom", "Stock/j", "Stock total", "Utilisés", "Reste", "Seuil", "Poids", "Actif", ""].map((h, i) => (
              <th key={i} className="text-left font-mono font-normal px-3 py-2 border-b border-[#2c323b] whitespace-nowrap">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const left = r.total_stock != null ? Math.max(0, r.total_stock - (r.used_total || 0)) : "∞";
              return (
                <tr key={r.id} className="border-b border-[#232830]">
                  <td className="px-3 py-2">{r.image ? <img src={r.image.startsWith("http") ? r.image : `${API.replace("/api", "")}${r.image}`} alt="" className="w-8 h-8 rounded object-cover" /> : <span className="text-[#7d8590]">—</span>}</td>
                  <td className="px-3 py-2 font-bold whitespace-nowrap">{r.name}</td>
                  <td className="px-3 py-2 tabular-nums">{r.daily_stock ?? "∞"}</td>
                  <td className="px-3 py-2 tabular-nums">{r.total_stock ?? "∞"}</td>
                  <td className="px-3 py-2 tabular-nums text-[#9aa3b2]">{r.used_total || 0}</td>
                  <td className="px-3 py-2 tabular-nums">{left}</td>
                  <td className="px-3 py-2 tabular-nums">{r.threshold ?? "—"}</td>
                  <td className="px-3 py-2 tabular-nums">{r.weight}</td>
                  <td className="px-3 py-2">{r.active ? <span className="text-[10px] text-[#3ecf6f]">Oui</span> : <span className="text-[10px] text-[#7d8590]">Non</span>}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button onClick={() => setEdit({ reward: { ...emptyReward(bonus), ...r }, id: r.id })} className="text-[#F9B81E] p-1"><Pencil size={15} /></button>
                    <button onClick={() => del(r.id)} className="text-red-400 p-1"><Trash2 size={15} /></button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan="10" className="px-3 py-6 text-center text-[#7d8590]">Aucune récompense.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={edit.id ? "Modifier la récompense" : "Nouvelle récompense"} onClose={() => setEdit(null)} wide>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nom"><input value={edit.reward.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Field>
            <Field label="Image"><MediaUpload client={client} value={edit.reward.image} onChange={(v) => set("image", v)} onAuthErr={onAuthErr} /></Field>
            <label className={`${labelCls} sm:col-span-2`}>Description
              <textarea value={edit.reward.description} onChange={(e) => set("description", e.target.value)} rows={2} className={inputCls} /></label>
            <NumField label="Lots / jour" value={edit.reward.daily_stock} onChange={(v) => set("daily_stock", v)} min={0} hint="vide = illimité" />
            <NumField label="Lots / mois" value={edit.reward.monthly_stock} onChange={(v) => set("monthly_stock", v)} min={0} hint="vide = illimité" />
            <NumField label="Stock total" value={edit.reward.total_stock} onChange={(v) => set("total_stock", v)} min={0} hint="vide = illimité" />
            <Field label="Actif du"><input type="date" value={edit.reward.active_from || ""} onChange={(e) => set("active_from", e.target.value || null)} className={inputCls} /></Field>
            <Field label="Actif au"><input type="date" value={edit.reward.active_to || ""} onChange={(e) => set("active_to", e.target.value || null)} className={inputCls} /></Field>
            <div className="sm:col-span-2">
              <span className={labelCls}>Jours autorisés</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {DOW_IDX.map((d) => (
                  <button key={d} type="button" onClick={() => toggleDay(d)}
                    className={`text-xs px-2.5 py-1 rounded-full border ${edit.reward.days.includes(d) ? "bg-[#F9B81E] text-[#1a1206] border-[#F9B81E] font-bold" : "bg-white/5 text-[#9aa3b2] border-[#2c323b]"}`}>{DOW[d]}</button>
                ))}
              </div>
              <span className="block text-[10px] text-[#6b7480] mt-1">Aucun sélectionné = tous les jours</span>
            </div>
            <div className="sm:col-span-2">
              <span className={labelCls}>Difficultés (où ce lot peut être gagné)</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {DIFF_OPTS.map(([lvl, lbl]) => (
                  <button key={lvl} type="button" onClick={() => toggleDiff(lvl)}
                    className={`text-xs px-2.5 py-1 rounded-full border ${(edit.reward.difficulties || []).includes(lvl) ? "bg-[#F9B81E] text-[#1a1206] border-[#F9B81E] font-bold" : "bg-white/5 text-[#9aa3b2] border-[#2c323b]"}`}>{lbl}</button>
                ))}
              </div>
              <span className="block text-[10px] text-[#6b7480] mt-1">Aucune sélectionnée = toutes les difficultés</span>
            </div>
            <NumField label="Poids (probabilité)" value={edit.reward.weight} onChange={(v) => set("weight", v)} min={0} step="0.1" />
            <NumField label="Gagnants max / jour" value={edit.reward.max_winners_day} onChange={(v) => set("max_winners_day", v)} min={0} hint="vide = illimité" />
            {bonus ? (
              <div className="sm:col-span-2">
                <span className={labelCls}>Seuil de score par difficulté (points à atteindre)</span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                  {DIFF_OPTS.map(([lvl, lbl]) => (
                    <NumField key={lvl} label={lbl} value={edit.reward.thresholds?.[lvl] ?? null}
                      onChange={(v) => setThr(lvl, v)} min={0} />
                  ))}
                </div>
                <span className="block text-[10px] text-[#6b7480] mt-1">Points à atteindre pour débloquer ce palier, propre à chaque niveau. Vide = seuil unique ci-dessous, sinon seuil par défaut du niveau. (Les difficultés où ce lot est gagnable se règlent plus haut.)</span>
                <div className="mt-2 max-w-[12rem]">
                  <NumField label="Seuil unique (fallback)" value={edit.reward.threshold} onChange={(v) => set("threshold", v)} min={0} hint="si aucune difficulté renseignée" />
                </div>
              </div>
            ) : (
              <NumField label="Seuil de score" value={edit.reward.threshold} onChange={(v) => set("threshold", v)} min={0} hint="vide = seuil difficulté/général" />
            )}
            <NumField label="Âge minimum" value={edit.reward.min_age} onChange={(v) => set("min_age", v)} min={0} />
            <NumField label="Quota / joueur" value={edit.reward.quota_player} onChange={(v) => set("quota_player", v)} min={0} />
            <NumField label="Quota / téléphone" value={edit.reward.quota_phone} onChange={(v) => set("quota_phone", v)} min={0} />
            <NumField label="Quota / device" value={edit.reward.quota_device} onChange={(v) => set("quota_device", v)} min={0} />
            <NumField label="Quota / IP" value={edit.reward.quota_ip} onChange={(v) => set("quota_ip", v)} min={0} />
            <label className={`${labelCls} sm:col-span-2`}>Conditions (mentions)
              <textarea value={edit.reward.conditions} onChange={(e) => set("conditions", e.target.value)} rows={2} className={inputCls} /></label>
            <label className="flex items-center gap-2 text-sm text-[#9aa3b2] sm:col-span-2">
              <input type="checkbox" checked={edit.reward.active} onChange={(e) => set("active", e.target.checked)} /> Récompense active
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setEdit(null)} className="text-sm text-[#9aa3b2] px-4">Annuler</button>
            <button onClick={save} className={btnPrimary}><Save size={15} /> Enregistrer</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ---------- 2. Planning quotidien ----------

const todayISO = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Martinique" });
const addDaysISO = (iso, n) => { const d = new Date(iso + "T12:00:00"); d.setDate(d.getDate() + n); return d.toLocaleDateString("en-CA"); };

function PlanCard({ title, day, client, catalog, onAuthErr }) {
  const [plan, setPlan] = useState(null);
  const [mode, setMode] = useState("visible");
  const [sel, setSel] = useState([]);
  const load = useCallback(async () => {
    try {
      const d = await adminDailyPlan(client, day);
      setPlan(d);
      setSel(d.plan.map((p) => p.reward_id));
      if (d.plan.length) setMode(d.plan[0].mode === "mystery" ? "mystery" : "visible");
    } catch (e) { handleErr(e, onAuthErr, "Chargement du planning"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);
  useEffect(() => { load(); }, [load]);

  const draw = async () => {
    try { await adminDailyPlanDraw(client, day); toast.success("Tirage effectué"); load(); }
    catch (e) { handleErr(e, onAuthErr, "Tirage impossible"); }
  };
  const saveManual = async () => {
    try { await adminDailyPlanSet(client, { day, reward_ids: sel, mode }); toast.success("Planning enregistré"); load(); }
    catch (e) { handleErr(e, onAuthErr, "Enregistrement impossible"); }
  };
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <div className={`${cardCls} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-black text-[#F9B81E]">{title}</h3>
          <span className="text-xs text-[#9aa3b2] tabular-nums">{day}</span>
        </div>
        <button onClick={draw} className="flex items-center gap-1.5 bg-white/10 rounded-lg px-3 py-1.5 text-xs font-bold"><Dice5 size={14} /> Tirer maintenant</button>
      </div>
      <div className="space-y-1.5 mb-3">
        {plan?.plan?.length ? plan.plan.map((p) => (
          <div key={(p.difficulty || "") + p.reward_id} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-1.5 text-sm">
            {p.difficulty && <span className="text-[10px] rounded-full px-2 py-0.5 bg-[#3a2a12] text-[#F9B81E] font-bold capitalize">{p.difficulty}</span>}
            <span className="flex-1">{p.name}</span>
            <span className={`text-[10px] rounded-full px-2 py-0.5 ${p.mode === "mystery" ? "bg-[#2a1440] text-[#c79bff]" : "bg-[#14283a] text-[#7cc3ff]"}`}>{p.mode === "mystery" ? "Mystère" : "Visible"}</span>
            <span className="text-[10px] text-[#7d8590]">{p.source}</span>
            <span className="text-[10px] text-[#9aa3b2] tabular-nums">{p.winners || 0} gagnants</span>
          </div>
        )) : <div className="text-xs text-[#7d8590] py-2">Aucun gain planifié.</div>}
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer text-xs font-bold text-[#9aa3b2]">Sélection manuelle</summary>
        <div className="mt-2">
          <div className="flex items-center gap-3 mb-2">
            <label className="flex items-center gap-1.5 text-xs"><input type="radio" checked={mode === "visible"} onChange={() => setMode("visible")} /> Visible</label>
            <label className="flex items-center gap-1.5 text-xs"><input type="radio" checked={mode === "mystery"} onChange={() => setMode("mystery")} /> Mystère</label>
          </div>
          <div className="max-h-48 overflow-y-auto space-y-1 mb-2">
            {catalog.map((r) => (
              <label key={r.id} className="flex items-center gap-2 text-xs bg-white/5 rounded px-2 py-1 cursor-pointer">
                <input type="checkbox" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} />
                <span className="flex-1">{r.name}</span>
                {!r.active && <span className="text-[9px] text-[#7d8590]">inactif</span>}
              </label>
            ))}
          </div>
          <button onClick={saveManual} className={`${btnPrimary} w-full justify-center`}><Save size={14} /> Enregistrer ce planning</button>
        </div>
      </details>
    </div>
  );
}

function PlanningSection({ client, onAuthErr }) {
  const [catalog, setCatalog] = useState([]);
  const [customDay, setCustomDay] = useState(addDaysISO(todayISO(), 2));
  const [history, setHistory] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        setCatalog(await adminRewards(client));
        setHistory((await adminDailyPlanHistory(client, 30)).history || []);
      } catch (e) { handleErr(e, onAuthErr, "Chargement"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const today = todayISO();

  return (
    <>
      <h2 className="text-lg font-black mb-4">Planning des gains</h2>
      <div className="grid md:grid-cols-2 gap-4 mb-6 max-w-4xl">
        <PlanCard title="Aujourd'hui" day={today} client={client} catalog={catalog} onAuthErr={onAuthErr} />
        <PlanCard title="Demain" day={addDaysISO(today, 1)} client={client} catalog={catalog} onAuthErr={onAuthErr} />
      </div>

      <h3 className="font-bold text-[#F9B81E] text-sm mb-2">Préparer une date à l'avance</h3>
      <div className="flex items-center gap-2 mb-3">
        <input type="date" value={customDay} onChange={(e) => setCustomDay(e.target.value)} className="bg-[#14171b] border border-[#2c323b] rounded-lg px-3 py-2 text-sm" />
      </div>
      <div className="max-w-xl mb-6">
        {customDay && <PlanCard key={customDay} title="Date choisie" day={customDay} client={client} catalog={catalog} onAuthErr={onAuthErr} />}
      </div>

      <h3 className="font-bold text-[#F9B81E] text-sm mb-2">Historique (30 jours)</h3>
      <div className="overflow-x-auto rounded-lg border border-[#232830] max-w-3xl">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase text-[#7d8590]">
            {["Date", "Gains", "Gagnants"].map((h) => <th key={h} className="text-left font-mono font-normal px-3 py-2 border-b border-[#2c323b]">{h}</th>)}
          </tr></thead>
          <tbody>
            {history.map((d) => (
              <tr key={d.day} className="border-b border-[#232830]">
                <td className="px-3 py-2 tabular-nums whitespace-nowrap">{d.day}</td>
                <td className="px-3 py-2">{d.rewards.map((r) => r.name).join(", ")}</td>
                <td className="px-3 py-2 tabular-nums">{d.rewards.reduce((a, r) => a + (r.winners || 0), 0)}</td>
              </tr>
            ))}
            {history.length === 0 && <tr><td colSpan="3" className="px-3 py-6 text-center text-[#7d8590]">Aucun historique.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------- 3. Difficulté ----------

const DIFF_LEVELS = ["facile", "normal", "difficile", "extreme"];
const DIFF_FIELDS = [
  ["speed", "Vitesse"], ["accel", "Accél."], ["max_speed", "Vitesse max"],
  ["obstacle_freq", "Fréq. obstacles"], ["item_freq", "Fréq. items"], ["item_value", "Valeur item"],
  ["threshold", "Seuil score"], ["lives", "Vies"],
];

const DIFF_LABEL = { facile: "Facile", normal: "Normal", difficile: "Difficile", extreme: "Extrême" };

function DifficultySection({ client, onAuthErr }) {
  const [s, setS] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [gains, setGains] = useState(null); // { levels, gains:{lvl:[ids]}, rewards:[{id,name,active}] }
  useEffect(() => {
    (async () => {
      try {
        setS(await adminRewardSettings(client));
        setRewards(await adminRewards(client));
        setGains(await adminDifficultyGains(client));
      } catch (e) { handleErr(e, onAuthErr, "Chargement"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDiff = (lvl, key, val) => setS((p) => ({ ...p, difficulty: { ...p.difficulty, [lvl]: { ...(p.difficulty?.[lvl] || {}), [key]: val === "" ? null : Number(val) } } }));

  // Coche/décoche un lot pour un niveau de difficulté (sauvegarde immédiate).
  const toggleGain = async (lvl, id) => {
    if (!gains) return;
    const cur = gains.gains[lvl] || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    setGains((g) => ({ ...g, gains: { ...g.gains, [lvl]: next } }));
    try { await adminSetDifficultyGains(client, lvl, next); }
    catch (e) { handleErr(e, onAuthErr, "Enregistrement impossible"); }
  };

  const save = async () => {
    try {
      await adminRewardSettingsSave(client, {
        general_threshold: s.general_threshold, difficulty: s.difficulty,
        draw_hour: s.draw_hour, tz_offset: s.tz_offset, rewards_per_day: s.rewards_per_day,
        selection_mode: s.selection_mode, mystery: s.mystery, fallback_reward_id: s.fallback_reward_id || null,
      });
      toast.success("Réglages enregistrés");
    } catch (e) { handleErr(e, onAuthErr, "Enregistrement impossible"); }
  };

  if (!s) return <div className="text-[#7d8590]">Chargement…</div>;

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-black">Difficulté & tirage Runner</h2>
        <button onClick={save} className={`${btnPrimary} ml-auto`}><Save size={15} /> Enregistrer</button>
      </div>

      {gains && (
        <div className="mb-6 max-w-5xl">
          <h3 className="font-black text-[#F9B81E] mb-1">🎁 Gains gagnables par difficulté</h3>
          <p className="text-xs text-[#7d8590] mb-3">Coche les lots que le joueur peut gagner à chaque niveau (le lot du jour est tiré parmi eux). Enregistrement immédiat.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {gains.levels.map((lvl) => (
              <div key={lvl} className={`${cardCls} p-3`}>
                <div className="font-bold text-sm mb-2">{DIFF_LABEL[lvl] || lvl}</div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {gains.rewards.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-xs bg-white/5 rounded px-2 py-1 cursor-pointer">
                      <input type="checkbox" checked={(gains.gains[lvl] || []).includes(r.id)} onChange={() => toggleGain(lvl, r.id)} />
                      <span className="flex-1 truncate">{r.name}</span>
                      {!r.active && <span className="text-[9px] text-[#7d8590]">inactif</span>}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6 max-w-4xl">
        <NumField label="Seuil général" value={s.general_threshold} onChange={(v) => setS({ ...s, general_threshold: v })} min={0} />
        <NumField label="Gains / jour" value={s.rewards_per_day} onChange={(v) => setS({ ...s, rewards_per_day: v })} min={0} />
        <NumField label="Heure de tirage" value={s.draw_hour} onChange={(v) => setS({ ...s, draw_hour: v })} min={0} hint="0–23" />
        <NumField label="Décalage TZ" value={s.tz_offset} onChange={(v) => setS({ ...s, tz_offset: v })} hint="Martinique = -4" />
        <Field label="Mode de sélection">
          <select value={s.selection_mode || "random"} onChange={(e) => setS({ ...s, selection_mode: e.target.value })} className={inputCls}>
            <option className="text-black" value="random">Aléatoire</option>
            <option className="text-black" value="weighted">Pondéré (poids)</option>
            <option className="text-black" value="manual">Manuel</option>
          </select>
        </Field>
        <Field label="Gain de secours">
          <select value={s.fallback_reward_id || ""} onChange={(e) => setS({ ...s, fallback_reward_id: e.target.value || null })} className={inputCls}>
            <option className="text-black" value="">Aucun</option>
            {rewards.map((r) => <option className="text-black" key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-[#9aa3b2] mt-5">
          <input type="checkbox" checked={!!s.mystery} onChange={(e) => setS({ ...s, mystery: e.target.checked })} /> Mode mystère global
        </label>
      </div>

      <h3 className="font-bold text-[#F9B81E] text-sm mb-2">Paramètres par niveau</h3>
      <div className="overflow-x-auto rounded-lg border border-[#232830] max-w-4xl">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase text-[#7d8590]">
            <th className="text-left font-mono font-normal px-3 py-2 border-b border-[#2c323b]">Niveau</th>
            {DIFF_FIELDS.map(([, lbl]) => <th key={lbl} className="text-left font-mono font-normal px-2 py-2 border-b border-[#2c323b] whitespace-nowrap">{lbl}</th>)}
          </tr></thead>
          <tbody>
            {DIFF_LEVELS.map((lvl) => (
              <tr key={lvl} className="border-b border-[#232830]">
                <td className="px-3 py-2 font-bold capitalize whitespace-nowrap">{lvl}</td>
                {DIFF_FIELDS.map(([key]) => (
                  <td key={key} className="px-2 py-1.5">
                    <input type="number" value={s.difficulty?.[lvl]?.[key] ?? ""} onChange={(e) => setDiff(lvl, key, e.target.value)}
                      className="w-20 bg-white/5 border border-[#2c323b] rounded px-2 py-1 text-white outline-none focus:border-[#F9B81E]" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------- 4. Publicités ----------

const emptyCampaign = () => ({
  name: "", partner: "", media_url: "", media_type: "image", click_url: "", active: true,
  start_date: null, end_date: null, hour_start: null, hour_end: null,
  scenes: [], placements: [], priority: 0, weight: 10, rotation: "weighted", max_impressions: null,
});
const emptyPlacement = () => ({
  scene: "runner", name: "", x: 0.5, y: 0.5, w: 0.2, h: 0.15, depth: 1, perspective: "",
  yum_protected: false, formats: ["png", "jpg", "webp"], mobile: true, animation: false, clickable: false,
});

function AdsSection({ client, onAuthErr }) {
  const [tab, setTab] = useState("campaigns");
  const TABS = [["campaigns", "Campagnes"], ["placements", "Emplacements"], ["stats", "Statistiques"]];
  return (
    <>
      <h2 className="text-lg font-black mb-3">Publicités</h2>
      <div className="flex gap-2 mb-4">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`text-sm px-4 py-1.5 rounded-full border ${tab === k ? "bg-[#F9B81E] text-[#1a1206] font-black border-[#F9B81E]" : "bg-[#14171b] text-[#9aa3b2] border-[#2c323b]"}`}>{l}</button>
        ))}
      </div>
      {tab === "campaigns" && <CampaignsTab client={client} onAuthErr={onAuthErr} />}
      {tab === "placements" && <PlacementsTab client={client} onAuthErr={onAuthErr} />}
      {tab === "stats" && <AdStatsTab client={client} onAuthErr={onAuthErr} />}
    </>
  );
}

function CampaignsTab({ client, onAuthErr }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => {
    try { setRows(await adminAdCampaigns(client)); } catch (e) { handleErr(e, onAuthErr, "Chargement"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setEdit((s) => ({ ...s, c: { ...s.c, [k]: v } }));
  const save = async () => {
    if (!edit.c.name.trim()) { toast.error("Nom requis"); return; }
    try {
      await adminAdCampaignSave(client, { ...edit.c, weight: Number(edit.c.weight) || 0, priority: Number(edit.c.priority) || 0 }, edit.id);
      toast.success("Campagne enregistrée"); setEdit(null); load();
    } catch (e) { handleErr(e, onAuthErr, "Enregistrement impossible"); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer cette campagne ?")) return; try { await adminAdCampaignDelete(client, id); load(); } catch (e) { handleErr(e, onAuthErr); } };

  return (
    <>
      <div className="flex mb-3"><button onClick={() => setEdit({ c: emptyCampaign(), id: null })} className={`${btnPrimary} ml-auto`}><Plus size={15} /> Nouvelle campagne</button></div>
      <div className="overflow-x-auto rounded-lg border border-[#232830]">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase text-[#7d8590]">
            {["Nom", "Partenaire", "Type", "Priorité", "Poids", "Affichages", "Active", ""].map((h) => <th key={h} className="text-left font-mono font-normal px-3 py-2 border-b border-[#2c323b] whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-[#232830]">
                <td className="px-3 py-2 font-bold whitespace-nowrap">{c.name}</td>
                <td className="px-3 py-2 text-[#9aa3b2]">{c.partner}</td>
                <td className="px-3 py-2">{c.media_type}</td>
                <td className="px-3 py-2 tabular-nums">{c.priority}</td>
                <td className="px-3 py-2 tabular-nums">{c.weight}</td>
                <td className="px-3 py-2 tabular-nums">{c.impressions_count || 0}</td>
                <td className="px-3 py-2">{c.active ? <span className="text-[10px] text-[#3ecf6f]">Oui</span> : <span className="text-[10px] text-[#7d8590]">Non</span>}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button onClick={() => setEdit({ c: { ...emptyCampaign(), ...c }, id: c.id })} className="text-[#F9B81E] p-1"><Pencil size={15} /></button>
                  <button onClick={() => del(c.id)} className="text-red-400 p-1"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="8" className="px-3 py-6 text-center text-[#7d8590]">Aucune campagne.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={edit.id ? "Modifier la campagne" : "Nouvelle campagne"} onClose={() => setEdit(null)} wide>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nom"><input value={edit.c.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Field>
            <Field label="Partenaire"><input value={edit.c.partner} onChange={(e) => set("partner", e.target.value)} className={inputCls} /></Field>
            <Field label="Média"><MediaUpload client={client} value={edit.c.media_url} onChange={(v) => set("media_url", v)} accept="image/*,video/mp4" onAuthErr={onAuthErr} /></Field>
            <Field label="Type de média">
              <select value={edit.c.media_type} onChange={(e) => set("media_type", e.target.value)} className={inputCls}>
                <option className="text-black" value="image">Image</option>
                <option className="text-black" value="video">Vidéo</option>
                <option className="text-black" value="animation">Animation</option>
              </select>
            </Field>
            <label className={`${labelCls} sm:col-span-2`}>URL de clic
              <input value={edit.c.click_url} onChange={(e) => set("click_url", e.target.value)} placeholder="https://…" className={inputCls} /></label>
            <Field label="Début"><input type="date" value={edit.c.start_date || ""} onChange={(e) => set("start_date", e.target.value || null)} className={inputCls} /></Field>
            <Field label="Fin"><input type="date" value={edit.c.end_date || ""} onChange={(e) => set("end_date", e.target.value || null)} className={inputCls} /></Field>
            <NumField label="Heure début" value={edit.c.hour_start} onChange={(v) => set("hour_start", v)} min={0} hint="0–23, vide = toute la journée" />
            <NumField label="Heure fin" value={edit.c.hour_end} onChange={(v) => set("hour_end", v)} min={0} hint="0–23" />
            <Field label="Scènes (séparées par virgule)" hint="ex: runner"><CSVList value={edit.c.scenes} onChange={(v) => set("scenes", v)} placeholder="runner" /></Field>
            <Field label="Emplacements autorisés" hint="ids séparés par virgule, vide = tous"><CSVList value={edit.c.placements} onChange={(v) => set("placements", v)} placeholder="billboard_01, ..." /></Field>
            <NumField label="Priorité" value={edit.c.priority} onChange={(v) => set("priority", v)} />
            <NumField label="Poids (rotation)" value={edit.c.weight} onChange={(v) => set("weight", v)} min={0} step="0.1" />
            <Field label="Rotation">
              <select value={edit.c.rotation} onChange={(e) => set("rotation", e.target.value)} className={inputCls}>
                <option className="text-black" value="weighted">Pondérée</option>
                <option className="text-black" value="random">Aléatoire</option>
                <option className="text-black" value="fixed">Fixe (poids max)</option>
              </select>
            </Field>
            <NumField label="Affichages max" value={edit.c.max_impressions} onChange={(v) => set("max_impressions", v)} min={0} hint="vide = illimité" />
            <label className="flex items-center gap-2 text-sm text-[#9aa3b2] sm:col-span-2">
              <input type="checkbox" checked={edit.c.active} onChange={(e) => set("active", e.target.checked)} /> Campagne active
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setEdit(null)} className="text-sm text-[#9aa3b2] px-4">Annuler</button>
            <button onClick={save} className={btnPrimary}><Save size={15} /> Enregistrer</button>
          </div>
        </Modal>
      )}
    </>
  );
}

function PlacementsTab({ client, onAuthErr }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => {
    try { setRows(await adminAdPlacements(client)); } catch (e) { handleErr(e, onAuthErr, "Chargement"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setEdit((s) => ({ ...s, p: { ...s.p, [k]: v } }));
  const save = async () => {
    if (!edit.p.name.trim()) { toast.error("Nom requis"); return; }
    try { await adminAdPlacementSave(client, edit.p, edit.id); toast.success("Emplacement enregistré"); setEdit(null); load(); }
    catch (e) { handleErr(e, onAuthErr, "Enregistrement impossible"); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer cet emplacement ?")) return; try { await adminAdPlacementDelete(client, id); load(); } catch (e) { handleErr(e, onAuthErr); } };
  const numP = (k, label, step = "0.01") => (
    <Field label={label}><input type="number" step={step} value={edit.p[k] ?? ""} onChange={(e) => set(k, e.target.value === "" ? 0 : Number(e.target.value))} className={inputCls} /></Field>
  );

  return (
    <>
      <div className="flex mb-3"><button onClick={() => setEdit({ p: emptyPlacement(), id: null })} className={`${btnPrimary} ml-auto`}><Plus size={15} /> Nouvel emplacement</button></div>
      <div className="overflow-x-auto rounded-lg border border-[#232830]">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase text-[#7d8590]">
            {["Scène", "Nom", "x", "y", "w", "h", "Depth", "Yüm réservé", "Cliquable", ""].map((h) => <th key={h} className="text-left font-mono font-normal px-3 py-2 border-b border-[#2c323b] whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-b border-[#232830]">
                <td className="px-3 py-2">{p.scene}</td>
                <td className="px-3 py-2 font-bold whitespace-nowrap">{p.name}</td>
                <td className="px-3 py-2 tabular-nums">{p.x}</td>
                <td className="px-3 py-2 tabular-nums">{p.y}</td>
                <td className="px-3 py-2 tabular-nums">{p.w}</td>
                <td className="px-3 py-2 tabular-nums">{p.h}</td>
                <td className="px-3 py-2 tabular-nums">{p.depth}</td>
                <td className="px-3 py-2">{p.yum_protected ? <span className="text-[10px] text-[#F9B81E]">Oui</span> : "—"}</td>
                <td className="px-3 py-2">{p.clickable ? <span className="text-[10px] text-[#3ecf6f]">Oui</span> : "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button onClick={() => setEdit({ p: { ...emptyPlacement(), ...p }, id: p.id })} className="text-[#F9B81E] p-1"><Pencil size={15} /></button>
                  <button onClick={() => del(p.id)} className="text-red-400 p-1"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="10" className="px-3 py-6 text-center text-[#7d8590]">Aucun emplacement.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={edit.id ? "Modifier l'emplacement" : "Nouvel emplacement"} onClose={() => setEdit(null)} wide>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Scène"><input value={edit.p.scene} onChange={(e) => set("scene", e.target.value)} className={inputCls} /></Field>
            <Field label="Nom"><input value={edit.p.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Field>
            {numP("x", "Position x (0–1)")}
            {numP("y", "Position y (0–1)")}
            {numP("w", "Largeur (0–1)")}
            {numP("h", "Hauteur (0–1)")}
            {numP("depth", "Depth (profondeur)", "1")}
            <Field label="Perspective"><input value={edit.p.perspective} onChange={(e) => set("perspective", e.target.value)} className={inputCls} /></Field>
            <Field label="Formats acceptés"><CSVList value={edit.p.formats} onChange={(v) => set("formats", v)} placeholder="png, jpg, webp" /></Field>
            <label className="flex items-center gap-2 text-sm text-[#9aa3b2]"><input type="checkbox" checked={edit.p.yum_protected} onChange={(e) => set("yum_protected", e.target.checked)} /> Réservé Yüm (jamais de pub externe)</label>
            <label className="flex items-center gap-2 text-sm text-[#9aa3b2]"><input type="checkbox" checked={edit.p.mobile} onChange={(e) => set("mobile", e.target.checked)} /> Visible sur mobile</label>
            <label className="flex items-center gap-2 text-sm text-[#9aa3b2]"><input type="checkbox" checked={edit.p.animation} onChange={(e) => set("animation", e.target.checked)} /> Animation autorisée</label>
            <label className="flex items-center gap-2 text-sm text-[#9aa3b2]"><input type="checkbox" checked={edit.p.clickable} onChange={(e) => set("clickable", e.target.checked)} /> Cliquable</label>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setEdit(null)} className="text-sm text-[#9aa3b2] px-4">Annuler</button>
            <button onClick={save} className={btnPrimary}><Save size={15} /> Enregistrer</button>
          </div>
        </Modal>
      )}
    </>
  );
}

function AdStatsTab({ client, onAuthErr }) {
  const [days, setDays] = useState(0);
  const [ads, setAds] = useState(null);
  const [rewards, setRewards] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        setAds(await adminAdsStats(client, days));
        setRewards(await adminRewardsStats(client));
      } catch (e) { handleErr(e, onAuthErr, "Chargement"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);
  const pct = (r) => `${(r * 100).toFixed(1)}%`;

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-[#9aa3b2]">Période :</span>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="bg-[#14171b] border border-[#2c323b] rounded-lg px-3 py-1.5 text-sm">
          <option value="0">Depuis le début</option><option value="1">Aujourd'hui</option><option value="7">7 jours</option><option value="30">30 jours</option>
        </select>
      </div>

      {ads && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5 max-w-4xl">
            {[
              { l: "Affichages", v: ads.global.impressions },
              { l: "Sessions", v: ads.global.sessions },
              { l: "Durée moy. (s)", v: ads.global.avg_seconds_visible },
              { l: "Clics", v: ads.global.clicks },
              { l: "CTR", v: pct(ads.global.ctr) },
            ].map((c) => (
              <div key={c.l} className={`${cardCls} p-3`}>
                <div className="text-2xl font-black text-[#F9B81E] tabular-nums">{c.v}</div>
                <div className="text-[11px] text-[#9aa3b2]">{c.l}</div>
              </div>
            ))}
          </div>
          <h3 className="font-bold text-[#F9B81E] text-sm mb-2">Par campagne</h3>
          <div className="overflow-x-auto rounded-lg border border-[#232830] mb-6 max-w-4xl">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase text-[#7d8590]">
                {["Campagne", "Partenaire", "Affichages", "Sessions", "Durée moy.", "Clics", "CTR"].map((h) => <th key={h} className="text-left font-mono font-normal px-3 py-2 border-b border-[#2c323b] whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {ads.per_campaign.map((c) => (
                  <tr key={c.campaign_id} className="border-b border-[#232830]">
                    <td className="px-3 py-2 font-bold whitespace-nowrap">{c.name}</td>
                    <td className="px-3 py-2 text-[#9aa3b2]">{c.partner}</td>
                    <td className="px-3 py-2 tabular-nums">{c.impressions}</td>
                    <td className="px-3 py-2 tabular-nums">{c.sessions}</td>
                    <td className="px-3 py-2 tabular-nums">{c.avg_seconds_visible}s</td>
                    <td className="px-3 py-2 tabular-nums">{c.clicks}</td>
                    <td className="px-3 py-2 tabular-nums">{pct(c.ctr)}</td>
                  </tr>
                ))}
                {ads.per_campaign.length === 0 && <tr><td colSpan="7" className="px-3 py-6 text-center text-[#7d8590]">Aucune donnée.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {rewards && (
        <>
          <h3 className="font-bold text-[#F9B81E] text-sm mb-2">Gains par récompense</h3>
          <div className="overflow-x-auto rounded-lg border border-[#232830] max-w-4xl">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase text-[#7d8590]">
                {["Récompense", "Gains total", "Gains jour", "Stock/j restant", "Utilisés total", "Taux"].map((h) => <th key={h} className="text-left font-mono font-normal px-3 py-2 border-b border-[#2c323b] whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {rewards.per_reward.map((r) => (
                  <tr key={r.reward_id} className="border-b border-[#232830]">
                    <td className="px-3 py-2 font-bold whitespace-nowrap">{r.name}{!r.active && <span className="text-[9px] text-[#7d8590] ml-1">(inactif)</span>}</td>
                    <td className="px-3 py-2 tabular-nums">{r.wins_total}</td>
                    <td className="px-3 py-2 tabular-nums">{r.wins_today}</td>
                    <td className="px-3 py-2 tabular-nums">{r.daily_stock_left ?? "∞"}</td>
                    <td className="px-3 py-2 tabular-nums">{r.used_total}</td>
                    <td className="px-3 py-2 tabular-nums">{pct(r.rate)}</td>
                  </tr>
                ))}
                {rewards.per_reward.length === 0 && <tr><td colSpan="6" className="px-3 py-6 text-center text-[#7d8590]">Aucune donnée.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
